import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Slide,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  Button,
  CircularProgress,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

// Componente de transición
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Ejemplos de prompts predefinidos
const PROMPT_EXAMPLES = [
  {
    categoria: "Agendamiento",
    icono: "📅",
    color: "#10b981",
    ejemplos: [
      {
        titulo: "Flujo básico de citas",
        prompt: "Crea un flujo para agendar citas médicas que pida nombre del paciente, motivo de consulta y permita seleccionar fecha y hora"
      },
      {
        titulo: "Confirmación de citas",
        prompt: "Un flujo para confirmar citas programadas donde el paciente pueda confirmar, reagendar o cancelar su cita"
      },
      {
        titulo: "Recordatorio de cita",
        prompt: "Flujo de recordatorio que informe al paciente su cita próxima y le permita confirmar asistencia"
      }
    ]
  },
  {
    categoria: "Atención al Paciente",
    icono: "🏥",
    color: "#6366f1",
    ejemplos: [
      {
        titulo: "Triaje inicial",
        prompt: "Un flujo de triaje que capture síntomas del paciente, determine nivel de urgencia y direccione apropiadamente"
      },
      {
        titulo: "Registro de paciente nuevo",
        prompt: "Flujo para registrar pacientes nuevos con nombre completo, fecha de nacimiento, cédula, teléfono y correo electrónico"
      },
      {
        titulo: "Consulta de horarios",
        prompt: "Flujo para que pacientes consulten horarios disponibles de diferentes especialidades médicas"
      }
    ]
  },
  {
    categoria: "Especialidades",
    icono: "👨‍⚕️",
    color: "#8b5cf6",
    ejemplos: [
      {
        titulo: "Dermatología",
        prompt: "Flujo para clínica dermatológica que permita enviar foto de lesión cutánea para pre-evaluación antes de agendar cita"
      },
      {
        titulo: "Pediatría",
        prompt: "Flujo para consultorio pediátrico con tono amigable que pregunte edad del niño, síntomas y si es urgente"
      },
      {
        titulo: "Odontología",
        prompt: "Flujo para clínica dental que pregunte tipo de consulta (limpieza, urgencia, estético) y agende según disponibilidad"
      }
    ]
  },
  {
    categoria: "Soporte",
    icono: "💬",
    color: "#f59e0b",
    ejemplos: [
      {
        titulo: "Preguntas frecuentes",
        prompt: "Flujo con menú de preguntas frecuentes sobre ubicación, horarios, formas de pago y seguros aceptados"
      },
      {
        titulo: "Escalación a humano",
        prompt: "Flujo que intente resolver dudas básicas y escale a atención humana cuando el paciente lo solicite o el bot no pueda ayudar"
      },
      {
        titulo: "Encuesta de satisfacción",
        prompt: "Flujo post-consulta que pregunte al paciente cómo fue su experiencia y capture comentarios"
      }
    ]
  }
];

const AsistenteFlujosDialog = ({
  open,
  onClose,
  onSendMessage,
  messages = [],
  loading = false,
  nodes = [],
  user
}) => {
  const [input, setInput] = useState("");
  const [copiedPrompt, setCopiedPrompt] = useState(null);
  const [activeTab, setActiveTab] = useState("chat"); // "chat" o "ejemplos"
  const chatRef = useRef(null);

  // Scroll automático al final del chat
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || loading) return;
    onSendMessage(input.trim());
    setInput("");
  };

  const handleUsePrompt = (prompt) => {
    setInput(prompt);
    setActiveTab("chat");
    setCopiedPrompt(prompt);
    setTimeout(() => setCopiedPrompt(null), 2000);
  };

  const handleCopyPrompt = (prompt) => {
    navigator.clipboard.writeText(prompt);
    setCopiedPrompt(prompt);
    setTimeout(() => setCopiedPrompt(null), 2000);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: 2.5,
          background: "#f8fafc",
          boxShadow: "0 8px 32px rgba(99, 102, 241, 0.15)",
          border: "none",
          overflow: "hidden",
          height: "60vh",
          maxHeight: 520
        }
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "linear-gradient(135deg, #1e3a5f 0%, #2d5a87 100%)",
          color: "#fff",
          py: 1.5,
          px: 2
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon sx={{ fontSize: 22, color: "#fbbf24" }} />
          <Typography sx={{ fontWeight: 700, fontSize: 16, letterSpacing: 0.3 }}>
            Asistente IA
          </Typography>
          <Chip
            label="IA"
            size="small"
            sx={{
              ml: 0.5,
              height: 20,
              background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
              color: "#1e3a5f",
              fontWeight: 700,
              fontSize: 10
            }}
          />
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      {/* Tabs */}
      <Box sx={{
        display: "flex",
        borderBottom: "1px solid #e2e8f0",
        background: "#fff"
      }}>
        <Button
          onClick={() => setActiveTab("chat")}
          size="small"
          sx={{
            flex: 1,
            py: 1,
            borderRadius: 0,
            borderBottom: activeTab === "chat" ? "2px solid #6366f1" : "2px solid transparent",
            color: activeTab === "chat" ? "#6366f1" : "#64748b",
            fontWeight: 600,
            fontSize: 13,
            "&:hover": { background: "#f1f5f9" }
          }}
          startIcon={<AutoFixHighIcon sx={{ fontSize: 18 }} />}
        >
          Chat
        </Button>
        <Button
          onClick={() => setActiveTab("ejemplos")}
          size="small"
          sx={{
            flex: 1,
            py: 1,
            borderRadius: 0,
            borderBottom: activeTab === "ejemplos" ? "2px solid #6366f1" : "2px solid transparent",
            color: activeTab === "ejemplos" ? "#6366f1" : "#64748b",
            fontWeight: 600,
            fontSize: 13,
            "&:hover": { background: "#f1f5f9" }
          }}
          startIcon={<LightbulbIcon sx={{ fontSize: 18 }} />}
        >
          Ejemplos
        </Button>
      </Box>

      <DialogContent sx={{ p: 0, display: "flex", flexDirection: "column", flex: 1, overflow: "hidden" }}>
        {activeTab === "chat" ? (
          <>
            {/* Área de mensajes */}
            <Box
              ref={chatRef}
              sx={{
                flex: 1,
                p: 1.5,
                overflowY: "auto",
                background: "#fff",
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {messages.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 36, color: "#cbd5e1", mb: 1 }} />
                  <Typography sx={{ color: "#64748b", fontWeight: 500, fontSize: 14, mb: 0.5 }}>
                    ¡Hola! Soy tu asistente con IA
                  </Typography>
                  <Typography sx={{ color: "#94a3b8", fontSize: 12 }}>
                    Descríbeme el flujo que necesitas
                  </Typography>
                </Box>
              ) : (
                messages.map((msg, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      alignSelf: msg.from === "user" ? "flex-end" : "flex-start",
                      maxWidth: "85%",
                    }}
                  >
                    <Box
                      sx={{
                        background: msg.from === "user"
                          ? "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)"
                          : "#f1f5f9",
                        color: msg.from === "user" ? "#fff" : "#334155",
                        borderRadius: msg.from === "user"
                          ? "14px 14px 4px 14px"
                          : "14px 14px 14px 4px",
                        px: 1.5,
                        py: 1,
                        fontSize: 13,
                        fontWeight: 500,
                        whiteSpace: "pre-wrap",
                        boxShadow: msg.from === "user"
                          ? "0 2px 6px rgba(99, 102, 241, 0.25)"
                          : "0 1px 2px rgba(0,0,0,0.08)"
                      }}
                    >
                      {msg.text}
                    </Box>

                    {/* Opciones si existen */}
                    {msg.options && (
                      <Box sx={{ mt: 0.75, display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                        {msg.options.map((opt, i) => (
                          <Button
                            key={i}
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (opt.action && typeof opt.action === 'function') {
                                opt.action();
                              }
                            }}
                            sx={{
                              borderRadius: 1.5,
                              fontWeight: 600,
                              fontSize: 11,
                              py: 0.3,
                              px: 1,
                              textTransform: "none",
                              borderColor: "#6366f1",
                              color: "#6366f1",
                              "&:hover": {
                                background: "#6366f1",
                                color: "#fff",
                                borderColor: "#6366f1"
                              }
                            }}
                          >
                            {opt.label}
                          </Button>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))
              )}

              {/* Loading indicator */}
              {loading && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, alignSelf: "flex-start" }}>
                  <CircularProgress size={16} sx={{ color: "#6366f1" }} />
                  <Typography sx={{ color: "#64748b", fontSize: 13 }}>
                    Procesando con IA...
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Input area */}
            <Box
              sx={{
                p: 1.5,
                borderTop: "1px solid #e2e8f0",
                background: "#f8fafc",
                display: "flex",
                gap: 1,
                alignItems: "flex-end",
              }}
            >
              <TextField
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Describe el flujo que necesitas..."
                multiline
                maxRows={2}
                size="small"
                fullWidth
                disabled={loading}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 1.5,
                    background: "#fff",
                    fontSize: 13
                  }
                }}
                onKeyDown={e => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
              />
              <Button
                type="button"
                variant="contained"
                disabled={!input.trim() || loading}
                onClick={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
                sx={{
                  borderRadius: 1.5,
                  minWidth: 40,
                  height: 40,
                  background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
                  "&:hover": { background: "linear-gradient(135deg, #4f46e5 0%, #4338ca 100%)" },
                  "&:disabled": { background: "#cbd5e1" }
                }}
              >
                <SendIcon sx={{ fontSize: 20 }} />
              </Button>
            </Box>
          </>
        ) : (
          /* Tab de Ejemplos */
          <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
            {PROMPT_EXAMPLES.map((categoria, catIdx) => (
              <Box key={catIdx} sx={{ mb: 3 }}>
                {/* Header de categoría */}
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1.5,
                  pb: 1,
                  borderBottom: `2px solid ${categoria.color}20`
                }}>
                  <Box sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    background: `${categoria.color}15`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 18
                  }}>
                    {categoria.icono}
                  </Box>
                  <Typography sx={{
                    fontWeight: 700,
                    color: categoria.color,
                    fontSize: 15
                  }}>
                    {categoria.categoria}
                  </Typography>
                </Box>

                {/* Ejemplos */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {categoria.ejemplos.map((ejemplo, ejIdx) => (
                    <Box
                      key={ejIdx}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        background: "#fff",
                        border: "1px solid #e2e8f0",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          borderColor: categoria.color,
                          boxShadow: `0 2px 8px ${categoria.color}20`
                        }
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{
                            fontWeight: 600,
                            color: "#1e3a5f",
                            fontSize: 14,
                            mb: 0.5
                          }}>
                            {ejemplo.titulo}
                          </Typography>
                          <Typography sx={{
                            color: "#64748b",
                            fontSize: 13,
                            lineHeight: 1.5
                          }}>
                            {ejemplo.prompt}
                          </Typography>
                        </Box>

                        {/* Botones de acción */}
                        <Box sx={{ display: "flex", gap: 0.5, flexShrink: 0 }}>
                          <Tooltip title="Copiar prompt" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleCopyPrompt(ejemplo.prompt)}
                              sx={{
                                background: copiedPrompt === ejemplo.prompt ? "#dcfce7" : "#f1f5f9",
                                color: copiedPrompt === ejemplo.prompt ? "#16a34a" : "#64748b",
                                "&:hover": { background: "#e2e8f0" }
                              }}
                            >
                              {copiedPrompt === ejemplo.prompt ? (
                                <CheckCircleIcon sx={{ fontSize: 18 }} />
                              ) : (
                                <ContentCopyIcon sx={{ fontSize: 18 }} />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Usar este prompt" arrow>
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleUsePrompt(ejemplo.prompt)}
                              sx={{
                                borderRadius: 1.5,
                                minWidth: 0,
                                px: 1.5,
                                py: 0.5,
                                fontSize: 12,
                                fontWeight: 600,
                                background: categoria.color,
                                "&:hover": { filter: "brightness(0.9)" }
                              }}
                            >
                              Usar
                            </Button>
                          </Tooltip>
                        </Box>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AsistenteFlujosDialog;
